import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Switch } from '@headlessui/react';
import { useGetFixedCostSettings } from '../../../hooks/api/FixedCostSettings/useGetFixedCostSettings';
import { useUpdateFixedCostSettings } from '../../../hooks/api/FixedCostSettings/useUpdateFixedCostSettings';
import { FixedCostSettings, MissingDataBehavior } from '../../../api/FixedCostSettings/fixedCostSettings';
import {
  DEFAULT_FIXED_COST_SETTINGS,
  SYSTEM_FIXED_COST_TIERS,
  calculateFixedCosts,
  effectiveRates,
  validateFixedCostSettings,
} from '../../../utility/fixedCostSettings';
import { classNames } from '../../../utility/utility';

const CURRENT_YEAR = new Date().getFullYear();
const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const toNumber = (value: string) => (value.trim() === '' ? NaN : Number(value));
const shown = (n: number | null) => (n == null || Number.isNaN(n) ? '' : String(n));

const inputClass = 'block rounded-md border-0 py-1 px-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:bg-gray-50 disabled:text-gray-400';

const Toggle: FC<{ checked: boolean, onChange: (v: boolean) => void, label: string, id: string }> = ({ checked, onChange, label, id }) => (
  <Switch
    id={id}
    checked={checked}
    onChange={onChange}
    className={classNames(
      checked ? 'bg-indigo-600' : 'bg-gray-200',
      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
    )}
  >
    <span className="sr-only">{label}</span>
    <span
      aria-hidden="true"
      className={classNames(
        checked ? 'translate-x-5' : 'translate-x-0',
        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
      )}
    />
  </Switch>
);

const SettingRow: FC<{ title: ReactNode, description: string, toggle: ReactNode, off?: boolean, children?: ReactNode }> = ({ title, description, toggle, off, children }) => (
  <div className="px-4 py-4">
    <div className="flex items-start justify-between gap-4">
      <div className={classNames(off ? 'opacity-60' : '')}>
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      {toggle}
    </div>
    {children && <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-600">{children}</div>}
  </div>
);

const FieldError: FC<{ message?: string }> = ({ message }) => (message ? <p className="w-full text-sm text-red-600">{message}</p> : null);

const FixedCostSettingsPage: FC = () => {
  const { data, isLoading, error: loadError } = useGetFixedCostSettings();
  const { mutateAsync: saveSettings, isPending: isSaving, error: saveError } = useUpdateFixedCostSettings();

  const [ form, setForm ] = useState<FixedCostSettings>(DEFAULT_FIXED_COST_SETTINGS);
  const [ status, setStatus ] = useState<string | null>(null);
  const [ sample, setSample ] = useState({ yearBuilt: '1978', sqft: '1850' });

  const tiers = data?.system_tiers ?? SYSTEM_FIXED_COST_TIERS;
  const defaults = data?.defaults ?? DEFAULT_FIXED_COST_SETTINGS;

  useEffect(() => {
    if (data?.settings) {
      const { rates, customized, ...settings } = data.settings;
      setForm(settings);
    }
  }, [ data ]);

  const update = (changes: Partial<FixedCostSettings>) => {
    setForm(prev => ({ ...prev, ...changes }));
    setStatus(null);
  };

  const rates = effectiveRates(form, tiers);
  const errors = validateFixedCostSettings(form, tiers.length);
  const hasErrors = Object.keys(errors).length > 0;

  const sampleProperty = {
    yearBuilt: sample.yearBuilt.trim() === '' ? null : Number(sample.yearBuilt),
    sqft: sample.sqft.trim() === '' ? null : Number(sample.sqft),
  };
  const systemPreview = useMemo(() => calculateFixedCosts(defaults, sampleProperty, CURRENT_YEAR, tiers), [ defaults, sample, tiers ]);
  const userPreview = hasErrors ? null : calculateFixedCosts(form, sampleProperty, CURRENT_YEAR, tiers);
  const difference = userPreview ? userPreview.estimated_fixed_costs - systemPreview.estimated_fixed_costs : 0;

  const isCustomized = JSON.stringify({ ...form, custom_rates: form.custom_rates_enabled ? rates : [] }) !==
    JSON.stringify({ ...defaults, custom_rates: defaults.custom_rates_enabled ? effectiveRates(defaults, tiers) : [] });

  const setRate = (index: number, value: string) => {
    const next = form.custom_rates.length === tiers.length ? [ ...form.custom_rates ] : tiers.map(t => t.rate);
    next[index] = toNumber(value);
    update({ custom_rates: next });
  };

  const toggleCustomRates = (enabled: boolean) => {
    update({
      custom_rates_enabled: enabled,
      custom_rates: enabled && form.custom_rates.length !== tiers.length ? tiers.map(t => t.rate) : form.custom_rates,
    });
  };

  const handleSave = async () => {
    if (hasErrors) return;
    await saveSettings(form);
    setStatus('Fixed cost settings saved. New figures apply the next time you open a property.');
  };

  const handleRestore = () => {
    setForm(defaults);
    setStatus('Defaults restored. Save to apply them.');
  };

  const missingBehaviorSelect = (id: string, value: MissingDataBehavior, onChange: (v: MissingDataBehavior) => void, fallbackText: string, disabled = false) => (
    <select id={id} value={value} disabled={disabled} onChange={e => onChange(e.target.value as MissingDataBehavior)} className={classNames(inputClass, 'pr-8')}>
      <option value="zero">Use $0 (same as system)</option>
      <option value="fallback">{fallbackText}</option>
    </select>
  );

  const trace = (): [string, string][] => {
    if (!userPreview) return [];
    const lines: [string, string][] = [];
    const ageText = {
      property: `${CURRENT_YEAR} - ${sampleProperty.yearBuilt} = ${userPreview.home_age} yrs`,
      off: 'Not used, fallback tier',
      fallback: 'Missing, fallback tier',
      missing: 'Missing, result is $0',
    }[userPreview.home_age_source];
    lines.push([ 'Home age', ageText ]);
    if (userPreview.tier_index != null) {
      lines.push([ 'Tier', `${tiers[userPreview.tier_index].label} yrs` ]);
      const rate = rates[userPreview.tier_index];
      lines.push([ 'Rate', `$${rate}/sqft${rate !== tiers[userPreview.tier_index].rate ? ' (yours)' : ''}` ]);
    }
    const sqftText = {
      property: `${userPreview.sqft?.toLocaleString()} sqft`,
      off: `${userPreview.sqft?.toLocaleString()} sqft (default size)`,
      fallback: `${userPreview.sqft?.toLocaleString()} sqft (default size)`,
      missing: 'Missing, result is $0',
    }[userPreview.sqft_source];
    lines.push([ 'Living area', sqftText ]);
    lines.push([ 'Base', usd(userPreview.base) ]);
    if (userPreview.contingency_amount > 0) lines.push([ `Contingency ${form.contingency_percent}%`, `+${usd(userPreview.contingency_amount)}` ]);
    if (userPreview.bound_applied === 'min') lines.push([ 'Raised to your minimum', usd(form.min_amount) ]);
    if (userPreview.bound_applied === 'max') lines.push([ 'Capped at your maximum', usd(form.max_amount ?? 0) ]);
    return lines;
  };

  if (loadError) {
    return <p className="px-2 text-sm text-red-600">Could not load your fixed cost settings. Refresh the page to try again.</p>;
  }

  return (
    <div className="px-2 pb-16 max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">Fixed Costs</h1>
          <p className="mt-2 text-sm text-gray-700 max-w-2xl">
            Fixed costs estimate the rehab and make-ready spend on a property. They are added to your total costs and lower
            your max bid on every approach set to <span className="font-semibold">System Calculation</span>. Choose which
            property data the estimate uses and adjust the rates to fit your market.
          </p>
        </div>
        <div className="flex gap-2">
          {data?.engine_version && <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">Engine {data.engine_version}</span>}
          <span className={classNames('rounded-full px-2.5 py-0.5 text-xs font-semibold', isCustomized ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700')}>
            {isCustomized ? 'Custom settings' : 'Using system defaults'}
          </span>
        </div>
      </div>

      {/* Data points */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-gray-900">Data points</h2>
        <p className="text-sm text-gray-500">What the estimate is built from.</p>
        <div className="mt-3 divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
          <SettingRow
            title="Home age (from Year Built)"
            description="Picks the $/sqft rate tier. Older homes get a higher rate. When off, every property uses the fallback tier."
            off={!form.use_home_age}
            toggle={<Toggle id="use_home_age" label="Use home age" checked={form.use_home_age} onChange={v => update({ use_home_age: v })} />}
          >
            <label htmlFor="missing_age_behavior">If Year Built is missing:</label>
            {missingBehaviorSelect('missing_age_behavior', form.missing_age_behavior, v => update({ missing_age_behavior: v }), 'Use the fallback tier', !form.use_home_age)}
            <label htmlFor="age_fallback_tier" className="ml-2">Fallback tier:</label>
            <select id="age_fallback_tier" value={form.age_fallback_tier} onChange={e => update({ age_fallback_tier: Number(e.target.value) })} className={classNames(inputClass, 'pr-8')}>
              {tiers.map(t => <option key={t.index} value={t.index}>{t.label} yrs (${rates[t.index]}/sqft)</option>)}
            </select>
          </SettingRow>

          <SettingRow
            title="Living area (sqft)"
            description="The rate is multiplied by this. When off, every property uses the default size."
            off={!form.use_sqft}
            toggle={<Toggle id="use_sqft" label="Use living area" checked={form.use_sqft} onChange={v => update({ use_sqft: v })} />}
          >
            <label htmlFor="missing_sqft_behavior">If sqft is missing:</label>
            {missingBehaviorSelect('missing_sqft_behavior', form.missing_sqft_behavior, v => update({ missing_sqft_behavior: v }), 'Use the default size', !form.use_sqft)}
            <label htmlFor="default_sqft" className="ml-2">Default size:</label>
            <input id="default_sqft" type="number" min={1} step={50} value={shown(form.default_sqft)} onChange={e => update({ default_sqft: toNumber(e.target.value) })} className={classNames(inputClass, 'w-24')} />
            <span>sqft</span>
            <FieldError message={errors.default_sqft} />
          </SettingRow>

          <SettingRow
            title="Contingency buffer"
            description="Adds a percentage on top of the base estimate for surprises."
            off={!form.contingency_enabled}
            toggle={<Toggle id="contingency_enabled" label="Use contingency buffer" checked={form.contingency_enabled} onChange={v => update({ contingency_enabled: v })} />}
          >
            <input id="contingency_percent" aria-label="Contingency percent" type="number" min={0} max={100} step={1} disabled={!form.contingency_enabled} value={shown(form.contingency_percent)} onChange={e => update({ contingency_percent: toNumber(e.target.value) })} className={classNames(inputClass, 'w-20')} />
            <span>% of the base estimate</span>
            <FieldError message={errors.contingency_percent} />
          </SettingRow>

          <SettingRow
            title="Minimum and maximum"
            description="Keeps the estimate inside a range you trust. Properties with missing data still show $0."
            off={!form.bounds_enabled}
            toggle={<Toggle id="bounds_enabled" label="Use minimum and maximum" checked={form.bounds_enabled} onChange={v => update({ bounds_enabled: v })} />}
          >
            <label htmlFor="min_amount">Min $</label>
            <input id="min_amount" type="number" min={0} step={500} disabled={!form.bounds_enabled} value={shown(form.min_amount)} onChange={e => update({ min_amount: toNumber(e.target.value) })} className={classNames(inputClass, 'w-28')} />
            <label htmlFor="max_amount" className="ml-2">Max $</label>
            <input id="max_amount" type="number" min={0} step={1000} placeholder="No max" disabled={!form.bounds_enabled} value={shown(form.max_amount)} onChange={e => update({ max_amount: e.target.value.trim() === '' ? null : toNumber(e.target.value) })} className={classNames(inputClass, 'w-28')} />
            <FieldError message={errors.min_amount || errors.max_amount} />
          </SettingRow>
        </div>
      </div>

      {/* Rate tiers */}
      <div className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Rate tiers ($ per sqft by home age)</h2>
            <p className="text-sm text-gray-500">The system rates are shown for reference. Turn on your rates to edit them.</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Toggle id="custom_rates_enabled" label="Use my rates" checked={form.custom_rates_enabled} onChange={toggleCustomRates} />
            <span>Use my rates</span>
          </div>
        </div>
        <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Home age</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">System</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">My rate</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Change</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tiers.map((tier, i) => {
                const mine = form.custom_rates_enabled ? form.custom_rates[i] : tier.rate;
                const change = Number.isFinite(mine) ? mine - tier.rate : 0;
                const isPreviewTier = userPreview?.tier_index === i;
                return (
                  <tr key={tier.index} className={isPreviewTier ? 'bg-indigo-50' : ''}>
                    <td className="px-4 py-2 text-gray-900">{tier.label} yrs</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">${tier.rate}</td>
                    <td className="px-4 py-2 text-right">
                      <span className="inline-flex items-center gap-1">$
                        <input aria-label={`My rate for ${tier.label} years`} type="number" min={0} step={1} disabled={!form.custom_rates_enabled} value={shown(mine)} onChange={e => setRate(i, e.target.value)} className={classNames(inputClass, 'w-20 text-right tabular-nums')} />
                      </span>
                    </td>
                    <td className={classNames('px-4 py-2 text-right tabular-nums', change !== 0 ? 'font-semibold text-amber-700' : 'text-gray-400')}>
                      {change === 0 ? '-' : `${change > 0 ? '+' : ''}${change}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <FieldError message={errors.custom_rates} />
        <button type="button" disabled={!form.custom_rates_enabled} onClick={() => update({ custom_rates: tiers.map(t => t.rate) })} className="mt-3 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:text-gray-400">
          Reset rates to system
        </button>
      </div>

      {/* Where applied */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-gray-900">Where fixed costs are applied</h2>
        <p className="text-sm text-gray-500">An approach set to "Do Not Use" never includes fixed costs, whatever these say.</p>
        <div className="mt-3 divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
          <SettingRow
            title="Max bid, ROI and net sheet"
            description="Include fixed costs in total costs on the decision page and the Quick Net Sheet."
            toggle={<Toggle id="apply_to_max_bid" label="Apply to max bid" checked={form.apply_to_max_bid} onChange={v => update({ apply_to_max_bid: v })} />}
          />
          <SettingRow
            title="Approach conditions"
            description='Let conditions such as "fixed costs < $50,000" decide which approaches apply. When off, those conditions are skipped.'
            toggle={<Toggle id="apply_to_conditions" label="Apply to approach conditions" checked={form.apply_to_conditions} onChange={v => update({ apply_to_conditions: v })} />}
          />
        </div>
      </div>

      {/* Preview */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-gray-900">Preview on a sample property</h2>
        <p className="text-sm text-gray-500">Change the sample to test your settings. Clear a field to see what happens when that data is missing.</p>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 text-sm">
              <label htmlFor="sample_year_built" className="text-gray-600">Year built</label>
              <input id="sample_year_built" type="number" value={sample.yearBuilt} onChange={e => setSample(s => ({ ...s, yearBuilt: e.target.value }))} className={classNames(inputClass, 'w-28')} />
            </div>
            <div className="flex items-center justify-between gap-3 text-sm">
              <label htmlFor="sample_sqft" className="text-gray-600">Living area (sqft)</label>
              <input id="sample_sqft" type="number" value={sample.sqft} onChange={e => setSample(s => ({ ...s, sqft: e.target.value }))} className={classNames(inputClass, 'w-28')} />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">System</p>
                <p className="text-xl font-bold tabular-nums text-gray-900">{usd(systemPreview.estimated_fixed_costs)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your settings</p>
                <p className="text-xl font-bold tabular-nums text-gray-900">{userPreview ? usd(userPreview.estimated_fixed_costs) : '-'}</p>
                {userPreview && (
                  <p className={classNames('text-xs', difference === 0 ? 'text-gray-500' : difference > 0 ? 'text-amber-700' : 'text-green-700')}>
                    {difference === 0 ? 'Same as system' : `${difference > 0 ? '+' : '-'}${usd(Math.abs(difference))} vs system, max bid ${difference > 0 ? 'drops' : 'rises'} by ${usd(Math.abs(difference))}`}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">How your figure is built</p>
            {userPreview ? (
              <dl className="mt-2 font-mono text-xs">
                {trace().map(([ k, v ]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-dashed border-gray-200 py-1">
                    <dt className="text-gray-600">{k}</dt><dd className="text-right tabular-nums text-gray-900">{v}</dd>
                  </div>
                ))}
                <div className="flex justify-between gap-3 pt-2 text-sm font-semibold">
                  <dt>Fixed costs</dt><dd className="tabular-nums">{usd(userPreview.estimated_fixed_costs)}</dd>
                </div>
              </dl>
            ) : <p className="mt-2 text-sm text-red-600">Fix the highlighted fields to see a preview.</p>}
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-5">
        <p className="mr-auto text-sm text-gray-500">
          {saveError ? <span className="text-red-600">Could not save your settings. Check the fields and try again.</span>
            : status ?? 'Manual fixed costs you enter on a deal are never replaced.'}
        </p>
        <button type="button" onClick={handleRestore} disabled={isLoading} className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          Restore defaults
        </button>
        <button
          type="button"
          disabled={isLoading || isSaving || hasErrors}
          onClick={handleSave}
          className={classNames(
            'rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
            isLoading || isSaving || hasErrors ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-500 hover:cursor-pointer'
          )}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default FixedCostSettingsPage;
